(function () {
  const MOBILE_QUERY = '(max-width: 989px)';
  const navSelector = '.kenze-dynamic-nav';
  const itemSelector = '.kenze-dynamic-nav__item--has-dropdown';
  const toggleSelector = '.kenze-dynamic-nav__toggle';
  const dropdownSelector = '.kenze-dynamic-nav__dropdown';
  let dropdownId = 0;
  const dropdownByItem = new WeakMap();
  const placeholderByItem = new WeakMap();

  function isMobileNav() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function setDropdownTop(nav) {
    if (!nav || !isMobileNav()) return;
    const shell = nav.closest('.kenze-nav-shell') || nav;
    const rect = shell.getBoundingClientRect();
    document.documentElement.style.setProperty('--kenze-mobile-dropdown-top', `${Math.max(0, rect.bottom + 8)}px`);
  }

  function getItems(nav) {
    return Array.from(nav.querySelectorAll(itemSelector));
  }

  function getDropdown(item) {
    return dropdownByItem.get(item) || item?.querySelector(dropdownSelector) || null;
  }

  function portalDropdown(item, dropdown) {
    if (!item || !dropdown || !isMobileNav() || dropdown.parentElement === document.body) return;
    const marker = document.createComment('kenze-dropdown-placeholder');
    placeholderByItem.set(item, marker);
    dropdown.parentNode.insertBefore(marker, dropdown);
    document.body.appendChild(dropdown);
    dropdown.classList.add('kenze-mobile-dropdown-portal');
  }

  function restoreDropdown(item) {
    const dropdown = getDropdown(item);
    const marker = placeholderByItem.get(item);
    if (dropdown && marker && marker.parentNode) {
      marker.parentNode.insertBefore(dropdown, marker);
      marker.remove();
    }
    if (dropdown) dropdown.classList.remove('kenze-mobile-dropdown-portal');
    placeholderByItem.delete(item);
  }

  function closeItem(item) {
    if (!item) return;
    item.classList.remove('is-open');
    const button = item.querySelector(toggleSelector);
    const dropdown = getDropdown(item);
    if (button) button.setAttribute('aria-expanded', 'false');
    if (dropdown) { dropdown.removeAttribute('data-kenze-open'); dropdown.inert = true; }
    restoreDropdown(item);
  }

  function closeAll(nav, except) {
    if (!nav) return;
    getItems(nav).forEach((item) => {
      if (item !== except) closeItem(item);
    });
    const hasOpen = Boolean(nav.querySelector(`${itemSelector}.is-open`));
    document.body.classList.toggle('kenze-mobile-menu-open', hasOpen);
  }

  function openItem(nav, item) {
    const button = item.querySelector(toggleSelector);
    const dropdown = getDropdown(item);
    if (!button || !dropdown) return;

    closeAll(nav, item);
    item.classList.add('is-open');
    button.setAttribute('aria-expanded', 'true');
    if (isMobileNav()) portalDropdown(item, dropdown);
    dropdown.setAttribute('data-kenze-open', 'true');
    dropdown.inert = false;
    setDropdownTop(nav);
    document.body.classList.toggle('kenze-mobile-menu-open', isMobileNav());
  }

  function toggleItem(nav, item) {
    if (item.classList.contains('is-open')) {
      closeItem(item);
      closeAll(nav);
    } else {
      openItem(nav, item);
    }
  }

  function prepareNav(nav) {
    if (!nav || nav.dataset.kenzeMobileNavPrepared === 'true') return;
    nav.dataset.kenzeMobileNavPrepared = 'true';

    getItems(nav).forEach((item) => {
      const button = item.querySelector(toggleSelector);
      const dropdown = item.querySelector(dropdownSelector);
      const label = item.querySelector('a')?.textContent?.trim() || 'menu';

      if (button) {
        button.setAttribute('aria-label', `Open ${label} menu`);
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('type', 'button');
      }
      if (dropdown) dropdownByItem.set(item, dropdown);
      if (button && dropdown) {
        if (!dropdown.id) {
          dropdown.id = `kenze-mobile-dropdown-${++dropdownId}`;
        }
        button.setAttribute('aria-controls', dropdown.id);
        dropdown.inert = true;
      }
      item.addEventListener('pointerenter', (event) => {
        if (!isMobileNav() && event.pointerType === 'mouse') openItem(nav, item);
      });
      item.addEventListener('pointerleave', () => {
        if (!isMobileNav() && !item.contains(document.activeElement)) closeItem(item);
      });
      item.addEventListener('focusout', (event) => {
        const currentDropdown = getDropdown(item);
        if (!item.contains(event.relatedTarget) && !currentDropdown?.contains(event.relatedTarget)) closeItem(item);
      });
    });

    nav.addEventListener('click', function (event) {
      const toggle = event.target.closest(toggleSelector);
      if (!toggle || !nav.contains(toggle)) return;

      const item = toggle.closest(itemSelector);
      if (!item) return;

      event.preventDefault();
      event.stopPropagation();
      toggleItem(nav, item);
    }, true);

    nav.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowDown' || !event.target.matches(toggleSelector)) return;
      event.preventDefault();
      const item = event.target.closest(itemSelector);
      openItem(nav, item);
      item.querySelector(`${dropdownSelector} a`)?.focus();
    });

    // On phones/tablets, the horizontal category rail must remain usable after
    // opening Bundles / Curated Ranges / Support. If the customer starts
    // scrolling the rail, close the open dropdown instead of trapping them in it.
    let navScrollFrame = 0;
    nav.addEventListener('scroll', function () {
      if (!isMobileNav() || !nav.querySelector(`${itemSelector}.is-open`)) return;
      if (navScrollFrame) return;
      navScrollFrame = requestAnimationFrame(() => {
        navScrollFrame = 0;
        closeAll(nav);
      });
    }, { passive: true });
  }

  function init() {
    document.querySelectorAll(navSelector).forEach(prepareNav);
  }

  document.addEventListener('click', function (event) {
    const nav = event.target.closest(navSelector);
    const dropdown = event.target.closest(dropdownSelector);
    if (!nav && !dropdown) {
      document.querySelectorAll(navSelector).forEach((currentNav) => closeAll(currentNav));
      document.body.classList.remove('kenze-mobile-menu-open');
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    document.querySelectorAll(navSelector).forEach((nav) => {
      const activeItem = document.activeElement?.closest(itemSelector);
      closeAll(nav);
      if (activeItem && nav.contains(activeItem)) activeItem.querySelector(toggleSelector)?.focus();
    });
    document.body.classList.remove('kenze-mobile-menu-open');
  });

  function refresh() {
    document.querySelectorAll(navSelector).forEach((nav) => {
      const openItemEl = nav.querySelector(`${itemSelector}.is-open`);
      if (!openItemEl) return;
      if (!isMobileNav()) {
        document.body.classList.remove('kenze-mobile-menu-open');
      } else {
        setDropdownTop(nav);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  let refreshFrame;
  const queueRefresh = () => {
    if (refreshFrame) return;
    refreshFrame = requestAnimationFrame(() => { refreshFrame = 0; refresh(); });
  };
  window.addEventListener('resize', queueRefresh, { passive: true });
  window.addEventListener('orientationchange', queueRefresh, { passive: true });
  window.addEventListener('scroll', queueRefresh, { passive: true });
})();
